import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { BaseDataService } from './base.data.service';
import { KPIMonthlyTotals } from '../interfaces/kpis/kpi-monthly-totals';
import { KPILivefeed } from '../interfaces/kpis/kpi-livefeed';
import { KPICommercialTotals } from '../interfaces/kpis/kpi-commercial-totals';
import { KPIOperationalTotals } from '../interfaces/kpis/kpi-operational-totals';
import { KPIFinancialTotals } from '../interfaces/kpis/kpi-financial-totals';
import FileSaver from 'file-saver';
import { Message } from '../classes/Message';
import moment from 'moment';
import { MessageService } from 'primeng';

@Injectable({ providedIn: 'root' })
export class KPIService extends BaseDataService {
  url = '/api/kpi';
  entityTitle = 'KPI';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  public getDashboardTabs(): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/getDashboardTabs`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getKPIMonthlyTotals(fromDate: Date, rangeInMonths: number): Promise<KPIMonthlyTotals[]> {
    return this.handleRequest(
      this.http.get<KPIMonthlyTotals[]>(
        `${this.url}/getKPIMonthlyTotals/${rangeInMonths}?fromDate=${fromDate.toUTCString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getKPICommercialTotals(
    fromDate: Date,
    rangeInMonths: number,
  ): Promise<KPICommercialTotals[]> {
    return this.handleRequest(
      this.http.get<KPICommercialTotals[]>(
        `${this.url}/getKPICommercialTotals/${rangeInMonths}?fromDate=${fromDate.toUTCString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getKPIOperationalTotals(
    fromDate: Date,
    rangeInMonths: number,
  ): Promise<KPIOperationalTotals[]> {
    return this.handleRequest(
      this.http.get<KPIOperationalTotals[]>(
        `${this.url}/getKPIOperationalTotals/${rangeInMonths}?fromDate=${fromDate.toUTCString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getKPIFinancialTotals(
    fromDate: Date,
    rangeInMonths: number,
  ): Promise<KPIFinancialTotals[]> {
    return this.handleRequest(
      this.http.get<KPIFinancialTotals[]>(
        `${this.url}/getKPIFinancialTotals/${rangeInMonths}?fromDate=${fromDate.toUTCString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getLiveFeedData(fromDate: Date, range: number, rangeType: number): Promise<KPILivefeed[]> {
    return this.handleRequest(
      this.http.get<KPILivefeed[]>(
        `${this.url}/getLiveFeedData/${range}/${rangeType}?fromDate=${fromDate.toUTCString()}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public downloadToCSV(startDate: Date, endDate: Date, isVME: boolean) {
    //_${moment().format("YYYYMMMMDo_h:mm")}
    let filename = `KPIs_VME_${moment(startDate).format('YYYY-MM-DD')}_to_${moment(endDate).format(
      'YYYY-MM-DD',
    )}.csv`;

    // let url = `${this.url}/${isVME ? 'VMEDownloadToCSV' : 'CustomerDownloadToCSV'}/${moment(startDate).format("YYYYMMDD")}/${moment(endDate).format("YYYYMMDD")}`;
    let url = `${this.url}/${
      isVME ? 'VMEDownloadToCSV' : 'CustomerDownloadToCSV'
    }/${startDate.toISOString()}/${endDate.toISOString()}`;
    let report = this.handleRequest(
      this.http.get(url, { responseType: 'blob' }),
      this.handlersFactory.getForReadAll(),
    )
      .then((result) => {
        this.handleDownloadFile(result, filename);
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      })
      .catch((err) => {
        this.messageService.add(Message.Error('Could not download the csv.'));
      });

    return report;
  }

  public handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  public getKPILastCacheRefreshTime(): Promise<string> {
    return this.http.get<string>(`${this.url}/getLatestCacheRefreshTime`).toPromise();
  }
}
